import React from 'react';
import { User, Star, CheckCircle, Car, Euro, MapPin, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SavingsBadge } from './SavingsBadge';

/**
 * Match result card that slides in below the pulsing Orb.
 * Drop-in animation: respects parent visibility; adds a smooth upward slide + fade.
 *
 * Props:
 *   match: {
 *     id, name, avatar_url?, rating, rating_count?, verified?, languages?,
 *     from_city, to_city, departure_time?, vehicle_class?, price_per_km?, total_price?,
 *     match_score?
 *   }
 *   onAccept(), onReject(), onViewProfile()
 */
export const MatchCard = ({ match, onAccept, onReject, onViewProfile }) => {
  const { t } = useTranslation();
  if (!match) return null;

  const score = Math.round((match.match_score || 0) * 100);

  return (
    <div
      data-testid="orb-match-card"
      style={styles.wrapper}
      className="orb-match-card-enter"
    >
      <div style={styles.card}>
        {/* Header row */}
        <div style={styles.headerRow}>
          <div style={styles.avatarBox}>
            {match.avatar_url ? (
              <img src={match.avatar_url} alt={match.name} style={styles.avatar} />
            ) : (
              <User size={26} color="#cbd5e1" />
            )}
            {match.verified && (
              <div style={styles.verifiedBadge} title={t('match.verified')}>
                <CheckCircle size={14} color="#22c55e" />
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.name} data-testid="match-name">{match.name}</div>
            <div style={styles.ratingRow}>
              <Star size={12} color="#fbbf24" fill="#fbbf24" />
              <span style={styles.rating}>{(match.rating ?? 0).toFixed(1)}</span>
              {match.rating_count != null && (
                <span style={styles.ratingCount}>({match.rating_count})</span>
              )}
              {match.languages?.length > 0 && (
                <span style={styles.langs}>· {match.languages.slice(0, 3).join(' · ')}</span>
              )}
            </div>
          </div>
          {score > 0 && (
            <div style={styles.scoreBadge} data-testid="match-score">
              <div style={styles.scoreValue}>{score}%</div>
              <div style={styles.scoreLabel}>{t('match.match')}</div>
            </div>
          )}
        </div>

        {/* Route */}
        <div style={styles.routeRow}>
          <MapPin size={14} color="#22c55e" />
          <span style={styles.routeText}>
            {match.from_city} → {match.to_city}
          </span>
          {match.departure_time && (
            <>
              <Clock size={13} color="#94a3b8" style={{ marginLeft: 8 }} />
              <span style={styles.timeText}>
                {new Date(match.departure_time).toLocaleString(undefined, {
                  weekday: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </>
          )}
        </div>

        {/* Vehicle + price */}
        <div style={styles.vehicleRow}>
          {match.vehicle_photo_url && (
            <img
              src={match.vehicle_photo_url}
              alt={match.vehicle_class || 'Vehicle'}
              data-testid="match-vehicle-photo"
              style={styles.vehiclePhoto}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {match.vehicle_class && (
            <div style={styles.chip}>
              <Car size={13} /> {match.vehicle_class}
            </div>
          )}
          {match.price_per_km != null && (
            <div style={styles.chip}>
              <Euro size={13} /> {Number(match.price_per_km).toFixed(3)}/km
            </div>
          )}
          {match.total_price != null && (
            <div style={{ ...styles.chip, ...styles.priceChip }} data-testid="match-total-price">
              <Euro size={13} /> {Number(match.total_price).toFixed(2)}
            </div>
          )}
          {/* Phase C/P5 — Savings badge: only renders if leg ≥50km (tier <15%) */}
          {(match.total_price != null || match.price != null) && (
            <SavingsBadge
              ridePrice={Number(match.total_price ?? match.price ?? 0)}
              distanceKm={Number(match.distance_km ?? match.leg_distance_km ?? match.route_distance_km ?? 0)}
              compact={true}
            />
          )}
        </div>

        {/* Actions */}
        <div style={styles.actionsRow}>
          {onViewProfile && (
            <button
              type="button"
              onClick={onViewProfile}
              data-testid="match-view-profile"
              style={styles.secondaryBtn}
            >
              {t('match.viewProfile')}
            </button>
          )}
          {onReject && (
            <button
              type="button"
              onClick={onReject}
              data-testid="match-reject"
              style={styles.rejectBtn}
            >
              {t('match.decline')}
            </button>
          )}
          {onAccept && (
            <button
              type="button"
              onClick={onAccept}
              data-testid="match-accept"
              style={styles.acceptBtn}
            >
              {t('match.accept')}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes matchCardSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .orb-match-card-enter {
          animation: matchCardSlideUp 360ms cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>
    </div>
  );
};

const styles = {
  wrapper: {
    width: '100%', maxWidth: 460, margin: '32px auto 0',
    padding: '0 16px', pointerEvents: 'auto',
  },
  card: {
    background: 'linear-gradient(180deg, rgba(15,23,42,0.92), rgba(30,41,59,0.92))',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(0,212,255,0.25)',
    borderRadius: 20, padding: 18,
    boxShadow: '0 20px 60px rgba(0,0,0,0.45), 0 0 24px rgba(0,212,255,0.15)',
    color: '#f1f5f9',
  },
  headerRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 },
  avatarBox: {
    position: 'relative', width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg,#1e293b,#0f172a)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid rgba(148,163,184,0.25)',
  },
  avatar: { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' },
  verifiedBadge: {
    position: 'absolute', bottom: -2, right: -2, background: '#0f172a',
    borderRadius: '50%', padding: 2, border: '1px solid rgba(34,197,94,0.4)',
  },
  name: { fontSize: 16, fontWeight: 700, color: '#f8fafc' },
  ratingRow: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontSize: 12 },
  rating: { color: '#fbbf24', fontWeight: 600 },
  ratingCount: { color: '#64748b' },
  langs: { color: '#94a3b8', marginLeft: 2 },
  scoreBadge: {
    textAlign: 'center',
    background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(0,212,255,0.15))',
    border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12,
    padding: '6px 10px', minWidth: 58,
  },
  scoreValue: { fontSize: 16, fontWeight: 800, color: '#22c55e', lineHeight: 1 },
  scoreLabel: { fontSize: 9, color: '#86efac', textTransform: 'uppercase', letterSpacing: 0.5 },
  routeRow: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
    background: 'rgba(255,255,255,0.03)', borderRadius: 10,
    marginBottom: 10, fontSize: 13,
  },
  routeText: { color: '#f1f5f9', fontWeight: 600 },
  timeText: { color: '#94a3b8', fontSize: 12 },
  vehicleRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  vehiclePhoto: {
    width: 64, height: 44, borderRadius: 8, objectFit: 'cover',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
    marginRight: 4,
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 999,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#cbd5e1', fontSize: 11, fontWeight: 500,
  },
  priceChip: {
    background: 'rgba(0,212,255,0.12)', color: '#67e8f9',
    border: '1px solid rgba(0,212,255,0.3)', fontWeight: 700,
  },
  actionsRow: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  secondaryBtn: {
    padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
    background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
    color: '#cbd5e1', fontSize: 13,
  },
  rejectBtn: {
    padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5', fontSize: 13, fontWeight: 600,
  },
  acceptBtn: {
    padding: '8px 18px', borderRadius: 10, cursor: 'pointer',
    background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none',
    color: '#fff', fontSize: 13, fontWeight: 700,
    boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
  },
};
